import type { IWasteReturnTemplateMapping } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import {
    WasteReturnPairedColumnInputKeys,
    WasteReturnPairedColumnRoles,
    WasteReturnTableKeys,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import {
    EA_IMPORT_TABLE_MAX_ROWS,
    EA_IMPORT_TABLE_OVERFLOW_STRATEGY,
    EA_IMPORT_TABLE_ROW_MODE,
    EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS,
    type IEaWasteReturnFieldDefinition,
} from "./eaWasteReturnFieldDictionary";
import {
    createDefaultEaWasteReturnMapping,
    ensureListSources,
    ensurePairedColumnInputs,
} from "./wasteReturnTemplateMappingDefaults";
import { formatTableFieldAddress, parseTableFieldAddress } from "./tableAddressParser";

export interface IEaWasteReturnFieldRow {
    fieldKey: string;
    label: string;
    section: IEaWasteReturnFieldDefinition["section"];
    kind: IEaWasteReturnFieldDefinition["kind"];
    sheet: string;
    address: string;
}

function readPairedColumnSide(
    mapping: IWasteReturnTemplateMapping,
    pairKey: string,
    role: typeof WasteReturnPairedColumnRoles.Names | typeof WasteReturnPairedColumnRoles.Dobs,
    definition: IEaWasteReturnFieldDefinition
) {
    const pair = mapping.pairedColumnInputs?.[pairKey];
    const side = role === WasteReturnPairedColumnRoles.Names ? pair?.names : pair?.dobs;
    return {
        sheet: side?.sheet ?? definition.defaultSheet,
        address: side?.address ?? definition.defaultAddress,
    };
}

export function createDefaultFieldRows(): IEaWasteReturnFieldRow[] {
    return EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS.map((definition) => ({
        fieldKey: definition.fieldKey,
        label: definition.label,
        section: definition.section,
        kind: definition.kind,
        sheet: definition.defaultSheet,
        address: definition.defaultAddress,
    }));
}

export function mapMappingToFlatRows(mapping: IWasteReturnTemplateMapping): IEaWasteReturnFieldRow[] {
    return EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS.map((definition) => {
        if (definition.kind === "singleCell") {
            const cell = mapping.fixedCells[definition.mappingKey];
            return {
                fieldKey: definition.fieldKey,
                label: definition.label,
                section: definition.section,
                kind: definition.kind,
                sheet: cell?.sheet ?? definition.defaultSheet,
                address: cell?.address ?? definition.defaultAddress,
            };
        }

        if (definition.kind === "pairedColumnPart" && definition.pairKey && definition.pairRole) {
            const side = readPairedColumnSide(mapping, definition.pairKey, definition.pairRole, definition);
            return {
                fieldKey: definition.fieldKey,
                label: definition.label,
                section: definition.section,
                kind: definition.kind,
                sheet: side.sheet,
                address: side.address,
            };
        }

        const tableKey = definition.tableKey ?? WasteReturnTableKeys.WasteReceived;
        const table = mapping.tables[tableKey];
        const column = table?.columns[definition.mappingKey];
        const columnLetter = column?.column ?? definition.defaultAddress.replace(/\d+$/, "");
        const firstDataRow = table?.firstDataRow ?? (Number(definition.defaultAddress.replace(/^[A-Z]+/i, "")) || 8);

        return {
            fieldKey: definition.fieldKey,
            label: definition.label,
            section: definition.section,
            kind: definition.kind,
            sheet: table?.sheet ?? definition.defaultSheet,
            address: formatTableFieldAddress(columnLetter, firstDataRow),
        };
    });
}

export function mapFlatRowsToMapping(
    rows: IEaWasteReturnFieldRow[],
    baseMapping?: IWasteReturnTemplateMapping
): IWasteReturnTemplateMapping {
    let mapping = ensurePairedColumnInputs(ensureListSources(structuredClone(baseMapping ?? createDefaultEaWasteReturnMapping())));
    const rowByKey = new Map(rows.map((row) => [row.fieldKey, row]));
    const pairedColumnInputs = { ...(mapping.pairedColumnInputs ?? {}) };

    for (const definition of EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS) {
        const row = rowByKey.get(definition.fieldKey);
        const sheet = row?.sheet.trim() ?? definition.defaultSheet;
        const addressInput = row?.address.trim() ?? definition.defaultAddress;

        if (definition.kind === "singleCell") {
            mapping.fixedCells[definition.mappingKey] = {
                sheet,
                address: addressInput.toUpperCase(),
                valueFormatter: definition.valueFormatter,
                regFieldKey: definition.regFieldKey ?? null,
                maxLength: definition.maxLength ?? null,
            };
            continue;
        }

        if (definition.kind === "pairedColumnPart" && definition.pairKey && definition.pairRole) {
            const current = pairedColumnInputs[definition.pairKey] ?? {
                names: { sheet: "Technical Competence", address: "H11:H16" },
                dobs: { sheet: "Technical Competence", address: "I11:I16" },
            };
            const side = { sheet, address: addressInput.toUpperCase() };
            pairedColumnInputs[definition.pairKey] =
                definition.pairRole === WasteReturnPairedColumnRoles.Names
                    ? { ...current, names: side }
                    : { ...current, dobs: side };
            continue;
        }

        const tableKey = definition.tableKey ?? WasteReturnTableKeys.WasteReceived;
        const parsed = parseTableFieldAddress(addressInput);
        if (!parsed.success) {
            continue;
        }

        const existingTable = mapping.tables[tableKey];
        mapping.tables[tableKey] = {
            sheet,
            firstDataRow: parsed.value.firstDataRow,
            rowMode: EA_IMPORT_TABLE_ROW_MODE,
            maxRows: EA_IMPORT_TABLE_MAX_ROWS,
            overflowStrategy: EA_IMPORT_TABLE_OVERFLOW_STRATEGY,
            columns: {
                ...(existingTable?.columns ?? {}),
                [definition.mappingKey]: {
                    column: parsed.value.column,
                    valueFormatter: definition.valueFormatter,
                    regFieldKey: definition.regFieldKey ?? null,
                },
            },
        };
    }

    mapping = { ...mapping, pairedColumnInputs };
    syncTableColumnsFromDefinitions(mapping, WasteReturnTableKeys.WasteReceived);
    syncTableColumnsFromDefinitions(mapping, WasteReturnTableKeys.WasteRemoved);

    return mapping;
}

function syncTableColumnsFromDefinitions(
    mapping: IWasteReturnTemplateMapping,
    tableKey: typeof WasteReturnTableKeys.WasteReceived | typeof WasteReturnTableKeys.WasteRemoved
): void {
    const table = mapping.tables[tableKey];
    if (!table) return;

    const definitions = EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS.filter(
        (definition) => definition.kind === "tableColumn" && definition.tableKey === tableKey
    );

    for (const definition of definitions) {
        const existing = table.columns[definition.mappingKey];
        if (!existing) {
            const parsed = parseTableFieldAddress(definition.defaultAddress);
            table.columns[definition.mappingKey] = {
                column: parsed.success ? parsed.value.column : definition.defaultAddress.replace(/\d+$/, "").toUpperCase(),
                valueFormatter: definition.valueFormatter,
                regFieldKey: definition.regFieldKey ?? null,
            };
        } else {
            table.columns[definition.mappingKey] = {
                ...existing,
                valueFormatter: definition.valueFormatter,
                regFieldKey: definition.regFieldKey ?? null,
            };
        }
    }

    table.rowMode = EA_IMPORT_TABLE_ROW_MODE;
    table.maxRows = EA_IMPORT_TABLE_MAX_ROWS;
    table.overflowStrategy = EA_IMPORT_TABLE_OVERFLOW_STRATEGY;
}
